import type { VendingInventoryComparison, VendingInventoryCompletedPackRow } from '../../../../types/vendingInventoryComparisonTypes';

export type VendingInventoryPackKey = string;

type VendingInventoryLine = {
  bin?: number | string | null;
  game?: number | string | null;
};

const normalizeKeyPart = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

export const toVendingInventoryPackKey = (line: VendingInventoryLine): VendingInventoryPackKey | null => {
  const bin = normalizeKeyPart(line?.bin);
  const game = normalizeKeyPart(line?.game);
  if (!bin || !game) return null;
  return `${bin}:${game}`;
};

export const buildVendingInventoryPackKeySet = (lines: unknown): Set<VendingInventoryPackKey> => {
  if (!Array.isArray(lines)) return new Set();
  const keys = lines
    .map((line) => toVendingInventoryPackKey((line || {}) as VendingInventoryLine))
    .filter((key): key is VendingInventoryPackKey => Boolean(key));
  return new Set(keys);
};

const toOptionalNumber = (value: unknown): number | null | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const buildPackRowMap = (lines: unknown): Map<VendingInventoryPackKey, VendingInventoryCompletedPackRow> => {
  const map = new Map<VendingInventoryPackKey, VendingInventoryCompletedPackRow>();
  if (!Array.isArray(lines)) return map;

  for (const line of lines) {
    const key = toVendingInventoryPackKey((line || {}) as VendingInventoryLine);
    if (!key) continue;

    const row = line as Record<string, unknown>;
    const [bin, gameId] = key.split(':');
    map.set(key, {
      key,
      bin,
      gameId,
      quantity: toOptionalNumber(row.quantity),
      value: toOptionalNumber(row.value),
    });
  }

  return map;
};

export const compareVendingInventoryPacks = (
  previousLines: unknown,
  currentLines: unknown,
): VendingInventoryComparison => {
  const previousKeys = buildVendingInventoryPackKeySet(previousLines);
  const currentKeys = buildVendingInventoryPackKeySet(currentLines);

  const newPackKeys = Array.from(currentKeys).filter((key) => !previousKeys.has(key)).sort();
  const completedPackKeys = Array.from(previousKeys).filter((key) => !currentKeys.has(key)).sort();

  const previousRowMap = buildPackRowMap(previousLines);
  const completedPacks = completedPackKeys
    .map((key) => previousRowMap.get(key))
    .filter((row): row is VendingInventoryCompletedPackRow => Boolean(row));

  return { newPackKeys, completedPacks };
};
